import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { getDemoOpsActiveShifts, type DemoOpsShiftRow } from "../../../Api/apiUtils";
import { Callout, Card, EmptyState, Loader, Page, PageHeader, Table } from "../../../ui";
import { useHasEmailAccess } from "../../../utils/emailAccessConfig";
import "./demo-ops.scss";

const DemoShiftActive: React.FC = () => {
  const { ready: accessReady, allowed } = useHasEmailAccess("demoShiftActive");
  const [rows, setRows] = useState<DemoOpsShiftRow[]>([]);
  const [loading, setLoading] = useState(false);

  // Current weekday + the next one (Sunday wraps to Monday)
  const days = useMemo(
    () => [moment().format("dddd"), moment().add(1, "day").format("dddd")],
    []
  );

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    setLoading(true);
    getDemoOpsActiveShifts(days)
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err) => {
        console.error("Failed to fetch active shifts", err);
        toast.error("Failed to load active shifts");
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, allowed]);

  if (!accessReady) {
    return (
      <Page>
        <Loader label="Loading permissions…" />
      </Page>
    );
  }

  if (!allowed) {
    return (
      <Page>
        <PageHeader eyebrow="Demo Ops" title="Demo Shift Active" />
        <Callout tone="danger" title="Access denied">
          You do not have permission to view Demo Shift Active.
        </Callout>
      </Page>
    );
  }

  return (
    <Page className="demo-ops">
      <PageHeader
        eyebrow="Demo Ops"
        title="Demo Shift Active"
        subtitle={`Agent shift status for ${days.join(" and ")}.`}
      />
      <Card title="Active shifts">
        {loading ? (
          <Loader label="Loading shifts..." />
        ) : rows.length > 0 ? (
          <div className="demo-ops-scroll">
            <Table caption="Active agent shifts">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Agent Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={String(r.id ?? `${r.agentName}-${r.day}-${i}`)}>
                    <td>{r.day ?? "-"}</td>
                    <td>{r.agentName ?? "-"}</td>
                    <td>{r.status ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <EmptyState title="No shifts found" />
        )}
      </Card>
    </Page>
  );
};

export default DemoShiftActive;
